/**
 * TanStack Query hook for GET /api/v1/admin/audit (Phase 12.4).
 *
 * Same shape as useAdminLogs but over ticket_events. The audit feed is
 * append-only history, so no auto-refresh is required by SPEC §9 - the
 * default just lets the page do an opportunistic refetch on focus.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { TicketEventQuery, TicketEventResponse } from "@/types/api";

export const ADMIN_AUDIT_QUERY_KEY = ["admin", "audit"] as const;

export interface UseAdminAuditEventsArgs {
  query: TicketEventQuery;
  enabled?: boolean;
}

export function useAdminAuditEvents(
  args: UseAdminAuditEventsArgs,
): UseQueryResult<TicketEventResponse[]> {
  const { query, enabled = true } = args;
  return useQuery({
    queryKey: [...ADMIN_AUDIT_QUERY_KEY, query],
    queryFn: async () => {
      const response = await api.get<TicketEventResponse[]>("/admin/audit", {
        params: stripEmpty(query),
      });
      return response.data;
    },
    enabled,
  });
}

function stripEmpty(q: TicketEventQuery): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    out[k] = v;
  }
  return out;
}
